import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { NotificationBell } from './NotificationBell';
import { ThemeToggle } from './ThemeToggle';
import LanguageSwitcher from '../common/LanguageSwitcher';
import TrialExpiredBanner from '../subscription/TrialExpiredBanner';
import FreeAccessBanner from '../subscription/FreeAccessBanner';
import EmailConfirmationBanner from '../subscription/EmailConfirmationBanner';
import { Menu, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Toaster } from '../ui/sonner';
import { OnboardingWizard } from '../onboarding/OnboardingWizard';
import { useAuth } from '../../contexts/AuthContext';

export function DashboardLayout() {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const location = useLocation();
  const isLandlord = user?.role === 'landlord';

  const handleRefresh = () => {
    setIsRefreshing(true);
    // Trigger a page reload to refresh data
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        onHelpClick={() => setShowOnboarding(true)}
      />
      <OnboardingWizard 
        externalOpen={showOnboarding} 
        onExternalClose={() => setShowOnboarding(false)} 
      />
      
      {/* Trial Expired Banner - Only for landlords */}
      {isLandlord && <TrialExpiredBanner />}
      
      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-lg border-b">
          <div className="flex items-center justify-between p-4">
            {/* Left: Mobile menu toggle */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden"
                data-testid="menu-toggle-btn"
              >
                <Menu className="w-6 h-6" />
              </Button>
              <h1 className="font-heading font-bold text-lg lg:hidden">U-DORM</h1>
            </div>
            
            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="text-muted-foreground hover:text-foreground"
                data-testid="refresh-page-btn"
                title="Refresh page"
              >
                <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
              <LanguageSwitcher variant="minimal" />
              <ThemeToggle />
              <NotificationBell />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          {isLandlord && <EmailConfirmationBanner />}
          {isLandlord && <FreeAccessBanner />}
          <Outlet />
        </main>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
}
